import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RadioInput } from 'components/form';
import { toTitleCase } from 'utils/titleCase';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';

type QualityOption = { title: string; description?: string };

type WorkplaceQualitiesFormData = {
  workplaceQualities: string[];
  qualityOptions: QualityOption[];
};

const workplaceQualitiesSchema = yup.object().shape({
  workplaceQualities: yup
    .array()
    .of(yup.string().required('Workplace quality option cannot be undefined'))
    .min(3, 'Select exactly 3 workplace qualities before saving')
    .max(3, 'Select exactly 3 workplace qualities before saving')
    .required('Workplace qualities is required'),
  qualityOptions: yup
    .array()
    .of(
      yup.object().shape({
        title: yup.string().required(),
        description: yup.string(),
      })
    )
    .default([]),
});

interface WorkplaceQualitiesEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: WorkplaceQualitiesFormData;
  onSave: (data: WorkplaceQualitiesFormData) => void;
  isSubmitting?: boolean;
}

export const WorkplaceQualitiesEditModal: React.FC<WorkplaceQualitiesEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting,
}) => {
  const defaultValues: WorkplaceQualitiesFormData = {
    workplaceQualities: currentData?.workplaceQualities || [],
    qualityOptions: currentData?.qualityOptions || [],
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<WorkplaceQualitiesFormData>({
    defaultValues,
    resolver: yupResolver(workplaceQualitiesSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData, reset]);

  const onSubmit = (data: WorkplaceQualitiesFormData) => {
    onSave(data);
  };

  const candidateQualitiesOptions = (currentData?.qualityOptions || []).map((option) => ({
    value: option.title,
    label: toTitleCase(option.title),
    description: option.description || '',
  }));
  const selectedQualities = watch('workplaceQualities') || [];
  const selectionMessage = selectedQualities.length !== 3
    ? 'Select exactly 3 workplace qualities before saving.'
    : '';

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Workplace Qualities"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[90%] mx-auto mt-[10px] mb-[30px]">
        <p className="mb-[20px] pb-2.5 border-b border-[#D0D5DD]">
          Select the three workplace qualities that best match the role
        </p>
        <h3 className="text-lg font-semibold text-[#1D2939] mb-1">Workplace Qualities</h3>

        {selectionMessage && (
          <small className="text-xs text-[#e74444] block mb-3">{selectionMessage}</small>
        )}

        {errors.workplaceQualities && typeof errors.workplaceQualities === 'object' && 'message' in errors.workplaceQualities && (
          <small className="text-xs text-[#e74444] block mb-3">{errors.workplaceQualities.message as string}</small>
        )}

        <RadioInput
          register={register}
          name="workplaceQualities"
          validatorMessage={errors.workplaceQualities?.message}
          options={candidateQualitiesOptions}
          type="checkbox"
          parentClassName="grid gap-[5%] !mb-0"
          checkValue={selectedQualities}
          disabled={selectedQualities.length >= 3}
        />
      </div>
    </BaseEditModal>
  );
};

export default WorkplaceQualitiesEditModal;
